<template>
  <div id="survey-draggable-list">
    <!--DRAGGABLE START -->
    <draggable
      :list="pages"
      draggable=".pages-draggable"
      handle=".handle"
      :group="{ name: 'pagesGroup' }"
      ghostClass="draggable-ghost"
      dragClass="draggable-ghost"
      @change="
        updatePageSort({
          survey_id: survey.id,
          ids: pages.map(({ id }) => id),
        })
      "
    >
      <div
        role="tablist"
        class="pages-draggable"
        v-for="(page, pageIndex) in pages"
        :key="pageIndex"
      >
        <b-card no-body class="accordion mb-1 border-0 rounded-0">
          <b-card-header
            header-tag="header"
            class="accordion-header"
            role="tab"
          >
            <div
              v-b-toggle="`page-accordion-${pageIndex}`"
              class="accordion-header-toggle"
            >
              <img
                src="@/assets/images/collapse.svg"
                alt="Collapsed Icon"
                class="d-inline-block mr-3 when-closed"
                style="width: 14px; height: 14px"
              />
              <img
                src="@/assets/images/expand.svg"
                alt="Expand Icon"
                class="d-inline-block mr-3 when-opened"
                style="width: 14px; height: 14px"
              />
            </div>
            <div class="accordion-header-contents">
              <b-container>
                <b-row>
                  <b-col>
                    <span class="page-name">{{ page["page_name"] }}</span>
                  </b-col>
                  <b-col
                    sm="5"
                    md="4"
                    class="text-right mt-2 mt-lg-0"
                    v-if="!isReadOnly"
                  >
                    <b-container>
                      <b-row>
                        <b-col cols="3" lg="2" class="ml-md-auto">
                          <i
                            class="action-btn"
                            @click="showAddAnalyticsModal(page.uuid)"
                          >
                            <img
                              src="@/assets/images/actions/code-light.svg"
                              alt="Code"
                            />
                          </i>
                        </b-col>
                        <b-col cols="3" lg="2">
                          <i class="action-btn handle">
                            <img
                              src="@/assets/images/actions/move-light.svg"
                              alt="Move"
                            />
                          </i>
                        </b-col>
                        <b-col cols="3" lg="2">
                          <i
                            class="action-btn"
                            @click="showEditPageModal(page.uuid)"
                          >
                            <img
                              src="@/assets/images/actions/gear-light.svg"
                              alt="Edit"
                            />
                          </i>
                        </b-col>
                        <b-col cols="3" lg="2">
                          <i
                            class="action-btn"
                            @click="showDeletePageModal(page.uuid)"
                          >
                            <img
                              src="@/assets/images/actions/delete_new.svg"
                              alt="Delete"
                            />
                          </i>
                        </b-col>
                      </b-row>
                    </b-container>
                  </b-col>
                </b-row>
              </b-container>
            </div>
          </b-card-header>
          <b-collapse
            :id="`page-accordion-${pageIndex}`"
            role="tabpanel"
            :visible="isDragging"
          >
            <b-card-body>
              <draggable
                :list="page.questions"
                draggable=".questions-draggable"
                handle=".handle"
                :group="{ name: 'questionsGroup' }"
                @start="isDragging = true"
                ghostClass="draggable-ghost"
                dragClass="draggable-ghost"
                @change="
                  sortQuestions({
                    page_uuid: page.uuid,
                    order: page.questions.map(({ uuid }) => uuid),
                  })
                "
              >
                <div
                  role="tablist"
                  class="questions-draggable"
                  v-for="(question, questionIndex) in page.questions"
                  :key="questionIndex"
                >
                  <b-card no-body class="accordion mb-1 border-0 rounded-0">
                    <b-card-header
                      header-tag="header"
                      class="accordion-header"
                      role="tab"
                    >
                      <div
                        v-b-toggle="
                          `question-accordion-${pageIndex}-${questionIndex}`
                        "
                        class="accordion-header-toggle"
                      >
                        <img
                          src="@/assets/images/collapse.svg"
                          alt="Collapsed Icon"
                          class="d-inline-block mr-3 when-closed"
                          style="width: 14px; height: 14px"
                        />
                        <img
                          src="@/assets/images/expand.svg"
                          alt="Expand Icon"
                          class="d-inline-block mr-3 when-opened"
                          style="width: 14px; height: 14px"
                        />
                      </div>
                      <div class="accordion-header-contents">
                        <b-container>
                          <b-row>
                            <b-col>
                              <span class="question-title">
                                {{ question.question !== "" && question.question !== "" ? question.question : question.title }}
                              </span>
                              <i class="guide-tooltip" :id="`guide-tooltip-${pageIndex}-${questionIndex}`" style="position: relative; top: 4px;"></i>
                              <b-popover
                                :target="`guide-tooltip-${pageIndex}-${questionIndex}`"
                                triggers="hover"
                                :content="tooltipMapping[question.question_type]"
                              ></b-popover>
                            </b-col>
                            <b-col
                              sm="5"
                              md="4"
                              class="text-right mt-2 mt-lg-0"
                              v-if="!isReadOnly"
                            >
                              <b-container>
                                <b-row>
                                  <b-col cols="3" lg="2" class="ml-md-auto">
                                    <switches
                                      v-model="question.is_enabled"
                                      theme="custom"
                                      color="primary"
                                      class="question-visibility-toggle"
                                    >
                                    </switches>
                                  </b-col>
                                  <b-col cols="3" lg="2">
                                    <i class="action-btn handle">
                                      <img
                                        src="@/assets/images/actions/move-light.svg"
                                        alt="Move"
                                      />
                                    </i>
                                  </b-col>
                                  <b-col cols="3" lg="2">
                                    <i
                                      class="action-btn"
                                      @click="
                                        createDuplicateQuestion({
                                          pageIndex,
                                          questionIndex,
                                        })
                                      "
                                    >
                                      <img
                                        src="@/assets/images/actions/duplicate.svg"
                                        alt="Duplicate"
                                      />
                                    </i>
                                  </b-col>
                                  <b-col cols="3" lg="2">
                                    <i
                                      class="action-btn"
                                      @click="
                                        showDeleteQuestionModal(
                                          page.uuid,
                                          question.uuid,
                                          questionIndex
                                        )
                                      "
                                    >
                                      <img
                                        src="@/assets/images/actions/delete_new.svg"
                                        alt="Delete"
                                      />
                                    </i>
                                  </b-col>
                                </b-row>
                              </b-container>
                            </b-col>
                          </b-row>
                        </b-container>
                      </div>
                    </b-card-header>
                    <b-collapse
                      :id="`question-accordion-${pageIndex}-${questionIndex}`"
                      role="tabpanel"
                    >
                      <b-card-body>
                        <!--FORMS START -->
                        <ContactDetailsForm
                          v-if="question.question_type === 'contact_details'"
                          :pageId="page.uuid"
                          :question="question"
                          :elements="question.contents.elements"
                          :index="`${pageIndex}-${questionIndex}`"
                          :key="formKey"
                          @reload="reloadComponent"
                        ><template slot="right-action-question-input"> <ImageFormAnswer v-if="!isReadOnly" @ImageData="getImageData" :showAddImageButton="!survey.pages[pageIndex].questions[questionIndex].image.image"  :imageKey="`${pageIndex}-${questionIndex}`"  :contentElementsLength="0" :showDeleteButton="false" componentKey="questionImage" @reload="reloadComponent"></ImageFormAnswer> </template>
                        <template slot="image-render"><ImageRender class="mt-3" v-if="survey.pages[pageIndex].questions[questionIndex].image.image" :showCloseButton="!isReadOnly"  :imgBase64="survey.pages[pageIndex].questions[questionIndex].image.image" :imageKey="`${pageIndex}-${questionIndex}`" :showImageContainer="survey.pages[pageIndex].questions[questionIndex].image.showImage" @close="deleteImageData(`${pageIndex}-${questionIndex}`)"></ImageRender></template>  
                        </ContactDetailsForm>
                        <MultipleChoiceForm
                          v-if="question.question_type === 'multiple_choice'
                            || question.question_type === 'checkbox'
                            || question.question_type === 'dropdown'"
                          :pageId="page.uuid"
                          :question="question"
                          :contents="question.contents"
                          :index="`${pageIndex}-${questionIndex}`"
                          :key="formKey"
                          @reload="reloadComponent"
                        ><template slot="right-action-question-input"> <ImageFormAnswer v-if="!isReadOnly" @ImageData="getImageData" :showAddImageButton="!survey.pages[pageIndex].questions[questionIndex].image.image"  :imageKey="`${pageIndex}-${questionIndex}`" :idPopup="Math.random()" :contentElementsLength="0" :showDeleteButton="false" componentKey="questionImage" @reload="reloadComponent"></ImageFormAnswer> </template>
                        <template slot="image-render"><ImageRender class="mt-3" v-if="survey.pages[pageIndex].questions[questionIndex].image.image" :showCloseButton="!isReadOnly"  :imgBase64="survey.pages[pageIndex].questions[questionIndex].image.image" :imageKey="`${pageIndex}-${questionIndex}`" :showImageContainer="survey.pages[pageIndex].questions[questionIndex].image.showImage"  @close="deleteImageData(`${pageIndex}-${questionIndex}`)"></ImageRender></template>  
                        </MultipleChoiceForm>
                        <OpenEndedForm
                          v-if="question.question_type === 'open_ended'"
                          :pageId="page.uuid"
                          :question="question"
                          :contents="question.contents"
                          :index="`${pageIndex}-${questionIndex}`"
                          :key="formKey"
                          @reload="reloadComponent"
                        ><template slot="right-action-question-input"> <ImageFormAnswer v-if="!isReadOnly" @ImageData="getImageData" :showAddImageButton="!survey.pages[pageIndex].questions[questionIndex].image.image"  :imageKey="`${pageIndex}-${questionIndex}`"  :contentElementsLength="0" :showDeleteButton="false" componentKey="questionImage" @reload="reloadComponent"></ImageFormAnswer> </template>
                        <template slot="image-render"><ImageRender class="mt-3" v-if="survey.pages[pageIndex].questions[questionIndex].image.image" :showCloseButton="!isReadOnly"  :imgBase64="survey.pages[pageIndex].questions[questionIndex].image.image" :imageKey="`${pageIndex}-${questionIndex}`" :showImageContainer="survey.pages[pageIndex].questions[questionIndex].image.showImage"  @close="deleteImageData(`${pageIndex}-${questionIndex}`)"></ImageRender></template> 
                        </OpenEndedForm>

                        <FileUploadForm
                          v-if="question.question_type === 'file_upload'"
                          :pageId="page.uuid"
                          :question="question"
                          :contents="question.contents"
                          :index="`${pageIndex}-${questionIndex}`"
                          :key="formKey"
                          @reload="reloadComponent"
                        ><template slot="right-action-question-input"> <ImageFormAnswer v-if="!isReadOnly" @ImageData="getImageData" :showAddImageButton="!survey.pages[pageIndex].questions[questionIndex].image.image"  :imageKey="`${pageIndex}-${questionIndex}`"  :contentElementsLength="0" :showDeleteButton="false" componentKey="questionImage" @reload="reloadComponent"></ImageFormAnswer> </template>
                        <template slot="image-render"><ImageRender class="mt-3" v-if="survey.pages[pageIndex].questions[questionIndex].image.image" :showCloseButton="!isReadOnly"  :imgBase64="survey.pages[pageIndex].questions[questionIndex].image.image" :imageKey="`${pageIndex}-${questionIndex}`" :showImageContainer="survey.pages[pageIndex].questions[questionIndex].image.showImage"  @close="deleteImageData(`${pageIndex}-${questionIndex}`)"></ImageRender></template> 
                        </FileUploadForm>
                        <RatingForm
                          v-if="question.question_type === 'rating'"
                          :pageId="page.uuid"
                          :question="question"
                          :contents="question.contents"
                          :index="`${pageIndex}-${questionIndex}`"
                          :key="formKey"
                          @reload="reloadComponent"
                        ><template slot="right-action-question-input"> <ImageFormAnswer v-if="!isReadOnly" @ImageData="getImageData" :showAddImageButton="!survey.pages[pageIndex].questions[questionIndex].image.image"  :imageKey="`${pageIndex}-${questionIndex}`"  :contentElementsLength="0" :showDeleteButton="false" componentKey="questionImage" @reload="reloadComponent"></ImageFormAnswer> </template>
                        <template slot="image-render"><ImageRender class="mt-3" v-if="survey.pages[pageIndex].questions[questionIndex].image.image" :showCloseButton="!isReadOnly"  :imgBase64="survey.pages[pageIndex].questions[questionIndex].image.image" :imageKey="`${pageIndex}-${questionIndex}`" :showImageContainer="survey.pages[pageIndex].questions[questionIndex].image.showImage"  @close="deleteImageData(`${pageIndex}-${questionIndex}`)"></ImageRender></template> 
                        </RatingForm>
                        <!--FORMS END -->
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>
              </draggable>
            </b-card-body>
          </b-collapse>
        </b-card>
        <b-row class="my-5">
          <b-col lg="2" class="mx-auto text-center text-lg-left">
            <b-button
              :id="`add-question-btn-${pageIndex}`"
              variant="primary btn-add-outline"
              :disabled="isReadOnly"
            >
              Add Question
            </b-button>
            <b-popover
              :target="`add-question-btn-${pageIndex}`"
              placement="bottom"
              container="survey-draggable-list"
              triggers="click blur"
            >
              <b-container>
                <b-row>
                  <b-col
                    v-for="(questionType, key) in questionTypes"
                    :key="key"
                    class="col-6"
                    @click="
                      $emit('add-question', {
                        type: questionType,
                        pageId: page.id,
                      });
                      $root.$emit('bv::hide::popover');
                    "
                  >
                    {{ questionType }}
                  </b-col>
                </b-row>
              </b-container>
            </b-popover>
          </b-col>
        </b-row>
      </div>
    </draggable>
    <!--DRAGGABLE END -->
    <!-- MODALS START -->
    <EditPageModal :pageId="pageId" />
    <DeletePageModal :pageId="pageId" />
    <DeleteQuestionModal />
      <ImageUploadAnswer
          :initialValue="imageInitialValue"
          :aspectRatio="imageAspectRatio" 
          :fileName="imageFileName" 
          :imageKey="currentImageKey"
          :fileType="imageFileType"
          :rawImageFile="imageRawFile"
          :imageWidth="setImageWidth"
          :imageHeight="setImageHeight"
          componentKey="questionImage"
          @newImageData="getNewImageData"
          @clearImage="deleteImageData(currentImageKey)"
          @closeModal="isCropperClose"
        />
    <!-- MODALS END-->
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import draggable from "vuedraggable";
import Switches from "vue-switches";
import ContactDetailsForm from "../forms/ContactDetailsForm";
import MultipleChoiceForm from "../forms/MultipleChoiceForm";
import OpenEndedForm from "../forms/OpenEndedForm";
import FileUploadForm from "../forms/FileUploadForm";
import RatingForm from "../forms/RatingForm";
import { startCase, camelCase, orderBy } from "lodash";
import EditPageModal from "../modals/EditPageModal";
import DeletePageModal from "../modals/DeletePageModal";
import DeleteQuestionModal from "../modals/DeleteQuestionModal";
import ImageFormAnswer from "@/components/survey/ImageFormAnswer.vue";
import ImageUploadAnswer from "@/components/survey/ImageUploadAnswer.vue";
import ImageRender from "@/components/survey/ImageRender.vue";

export default {
    name: "SurveyDraggableList",
    data() {
        return {
            pages: [],
            pageId: "",
            questionId: "",
            isDragging: false,
            questionTypes: [
                "Contact Details",
                "Dropdown",
                "Open-Ended",
                "Rating",
                "Multiple Choice",
                "File Upload",
                "Checkbox"
            ],
            tooltipMapping:  {
              "contact_details": "Contact Details - Collect customer contact information (e.g., name, email, phone).",
              "dropdown": "Dropdown - Customer selects one option from a dropdown menu.",
              "open_ended": "Open-Ended - Allow a free-form response from the customer.",
              "rating": "Rating - Customer rates on a scale (e.g., 1-5 stars).",
              "multiple_choice": "Multiple Choice - Customer selects one option from predefined choices.",
              "file_upload": "File Upload - Customer uploads a file as their response.",
              "checkbox": "Checkbox - Customers can select multiple options."
            },
            formKey: 0,
            currentImageKey: '0-0',
            imageInitialValue: null,
            imageAspectRatio: 0,
            imageFileName: '',
            imageFileType: '',
            imageRawFile: '',
            setImageWidth: '',
            setImageHeight: ''
        };
    },
    props: ["survey", "isUcoin"],
    computed: {
        ...mapGetters("surveys", ["questionProperties", "isReadOnly"]),
    },
    filters: {
        titleCase(value) {
            return startCase(camelCase(value));
        }
    },
    methods: {
        ...mapActions("surveys", [
            "createDuplicateQuestion",
            "updatePageSort",
            "updateQuestionSort",
            "getPageDetails",
            "errors"
        ]),
        ...mapMutations("surveys", [
            "setQuestionProperties",
            "setQuestionSortCount"
        ]),
        reloadComponent() {
          this.formKey += 1;
          this.$emit("reloadComponent") 
        },
        showEditPageModal(id) {
            this.$root.$emit("bv::show::modal", "edit-page-modal");
            this.pageId = id;
        },
        showDeletePageModal(id) {
            this.$root.$emit("bv::show::modal", "delete-page-modal");
            this.pageId = id;
        },
        showDeleteQuestionModal(pageId, questionId, questionIndex) {
            this.setQuestionProperties({
                questionIndex: questionIndex,
                questionId: questionId,
                page_uuid: pageId
            });
            this.$root.$emit("bv::show::modal", "delete-question-modal");
        },
        sortQuestions(obj) {
            this.setQuestionSortCount(0);
            obj.order.map(uuid => {
                if (uuid) {
                    const question = this.survey.pages
                        .find(page => page.uuid === obj.page_uuid)
                        .questions.find(question => question.uuid === uuid);
                    this.updateQuestionSort({
                        question_uuid: uuid,
                        params: {
                            page_uuid: obj.page_uuid,
                            sort: this.survey.pages
                                .find(page => page.uuid === obj.page_uuid)
                                .questions.indexOf(question)
                        }
                    });
                }
            });
        },
        formattedQuestionType(type) {
            return type.replace(/_/g, " ");
        },
        async showAddAnalyticsModal(uuid) {
            await this.getPageDetails(uuid).then(() => {
                if (
                    this.errors === "" ||
                    Object.keys(this.errors).length === 0
                )
                    this.$root.$emit("bv::show::modal", "analytics-code-modal");
            });
        },
      getImageData(data) {
          const splitKey = data.key.split('-');

          this.imageInitialValue = data.image;
          this.imageAspectRatio = data.aspectRatio;
          this.imageFileName = data.fileName;
          this.imageFileType = data.fileType;
           this.imageRawFile = data.rawFile;
          this.setImageWidth = data.width
        this.setImageHeight = data.height

        this.pages[splitKey[0]].questions[splitKey[1]] = {...this.pages[splitKey[0]].questions[splitKey[1]], image: data}
          this.currentImageKey = data.key;

          },
          deleteImageData(key) {
              const splitKey = key.split('-');
              
              this.$nextTick(() => {
                this.survey.pages[splitKey[0]].questions[splitKey[1]] ={...this.survey.pages[splitKey[0]].questions[splitKey[1]], "image":{ "key": key, "image": "", "aspectRatio": "", "prefix": "", "width": "", "height": "", "base64": "", "fileName": "", "fileType": "", "showImage": false }};
                this.reloadComponent();
              });
        },
        getNewImageData(data) {
          const splitKey = this.currentImageKey.split('-');
          this.pages[splitKey[0]].questions[splitKey[1]].image = {
            ...this.pages[splitKey[0]].questions[splitKey[1]].image, 
            prefix: data.prefix, 
            base64: data.base64, 
            image: data.image,
            showImage: data.showImage
          };
          this.imageInitialValue = data.image;
          // this.reloadComponent();
        },
        isCropperClose(data) {
          const splitKey = this.currentImageKey.split('-');
              this.$nextTick(() => {
                  this.pages[splitKey[0]].questions[splitKey[1]].image = {
                ...this.pages[splitKey[0]].questions[splitKey[1]].image, 
                showImage: data.showImage
                };
              this.reloadComponent();
              });
            
            
          }
    },
    components: {
        draggable,
        Switches,
        ContactDetailsForm,
        MultipleChoiceForm,
        OpenEndedForm,
        FileUploadForm,
        RatingForm,
        EditPageModal,
        DeletePageModal,
        DeleteQuestionModal,
        ImageFormAnswer,
        ImageUploadAnswer,
        ImageRender
    },
    created() {
        this.pages = this.survey.pages;
    }
};
</script>
<style scoped>
</style>
